import type { MergedOption } from "../../../services/util/optionRendering";
import { isTruthy } from "../../../util";
import type { RichGroupPickerProps } from "../components/RichGroupPickerV2";

type Group = RichGroupPickerProps["groups"][number];

export function getValueLabel(
  selectedValue: string,
  activeGroup: MergedOption | Group
): string {
  if (activeGroup.values) {
    const matchingObject = activeGroup.values.find(
      ({ value }) => value === selectedValue
    );
    return matchingObject?.label || selectedValue;
  }

  const result: string[] | undefined = activeGroup.groups
    ?.map((nestedGroup) => getValueLabel(selectedValue, nestedGroup))
    .filter(isTruthy);
  if (result?.length) return result[0];

  return selectedValue;
}

export function getActiveGroup(
  selectedValue: string,
  groups: Group[] | undefined | null
) {
  return groups?.find((group) => {
    if (group.groups) {
      return group.groups.some(
        (nestedGroup) =>
          nestedGroup.values?.some(({ value }) => value === selectedValue) ||
          nestedGroup.name === selectedValue
      );
    }

    return (
      group.values?.some(({ value }) => value === selectedValue) ||
      group.name === selectedValue
    );
  });
}

export function getGroupIdentifier(selectedValue: string) {
  return (group: Group) => {
    const getFirstValue = (targetGroup = group): string => {
      if (targetGroup.values?.length) return targetGroup.values[0].value;
      if (targetGroup.groups?.length)
        return getFirstValue(targetGroup.groups[0]);

      return targetGroup.name;
    };

    const getMatchingValue = (targetGroup = group): string | undefined => {
      const groupNameMatchesValue =
        !targetGroup.values?.length &&
        !targetGroup.groups?.length &&
        targetGroup.name === selectedValue;
      const groupContainsMatchingValue = targetGroup.values?.some(
        (option) => option.value === selectedValue
      );
      const subgroupContainsMatchingValue =
        targetGroup.groups?.some(getMatchingValue);

      if (
        groupNameMatchesValue ||
        groupContainsMatchingValue ||
        subgroupContainsMatchingValue
      )
        return selectedValue;

      return undefined;
    };

    const matchingValue = getMatchingValue();
    const firstValue = getFirstValue();
    return matchingValue || firstValue;
  };
}

export function makeGroupChangeHandler(
  name: string,
  groups: Group[],
  onChange: (name: string, value: string) => void,
  /** to use for tracking or any side effects based on the new value */
  callback?: (value: string) => void
) {
  return (_: string, value: string) => {
    const group = groups.find((o) => o.name === value);

    // Selected value is a member of a group
    // inaccessible at this level, activate it
    if (!group) {
      callback?.(value);
      onChange(name, value);
      return;
    }

    // Selected value is the name of a group
    // with values, activate first value
    if (group.values) {
      callback?.(value);
      onChange(name, group.values[0].value);
      return;
    }

    // Selected value is the name of a group
    // with nested groups
    if (group.groups) {
      callback?.(value);

      // Keep selecting the first child group until
      // there are no more child groups
      let currentFirstGroup = group;
      while (currentFirstGroup.groups) {
        [currentFirstGroup] = currentFirstGroup.groups;
      }

      // TODO: Refactor this for clarity and to remove the need for the assertion
      // Child group has values, activate the first one
      // This assertion is safe, as if it did have groups, this function would
      // have go into the `!group` path since a child group picker would go that route
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      onChange(name, currentFirstGroup.values![0].value);
      return;
    }

    callback?.(value);

    // Selected value is the name of a group
    // with no values or nested groups, activate its name
    onChange(name, group.name);
  };
}
